/**
 * The data environments add to nodes (`node.data`, by field id: the coding environment's
 * `language` and `code`), as plain functions: which values a box writes, which a search reads,
 * which choices a picker offers. The base names no field: they all come from the environment's
 * `fields` (its plugin's `plugin.kdl`).
 */
import type { FieldDef } from "@protocol/FieldDef";

/** Anything holding data the way a node (or a preset) does. */
export interface WithData {
  /** The values, by field id. */
  data?: { [key in string]: string };
}

/** The longest value counted as short: written on a line, searched, shown beside a name. */
export const SHORT_VALUE = 40;

/** Whether `value` fits a line: one line of at most {@link SHORT_VALUE} characters (a language, not code). */
export function isShort(value: string): boolean {
  return !value.includes("\n") && value.length <= SHORT_VALUE;
}

/** The value of field `id` held by `item` ("" when none). */
export function valueOf(item: WithData, id: string): string {
  return item.data?.[id] ?? "";
}

/** The values a class-diagram box writes under the name: those of the `fields` marked `on_box`, in their order. */
export function boxValues(item: WithData, fields: readonly FieldDef[]): string[] {
  return fields.filter((f) => f.on_box).flatMap((f) => (valueOf(item, f.id) ? [valueOf(item, f.id)] : []));
}

/** The short values of `item`, which a search looks in beside its name. */
export function shortValues(item: WithData): string[] {
  return Object.values(item.data ?? {}).filter((v) => !!v && isShort(v));
}

/** The values `item` holds that `fields` does not declare (an older file's, an environment's no longer on), by field id. */
export function undeclared(item: WithData, fields: readonly FieldDef[]): [string, string][] {
  return Object.entries(item.data ?? {}).filter(([key, value]) => !!value && !fields.some((f) => f.id === key));
}

/** The choices offered for `field`: its options, the ones the person `added`, and the values `items` use; once each, ignoring case, sorted. */
export function choicesOf(field: FieldDef, items: readonly WithData[], added: readonly string[] = []): string[] {
  const all = new Map<string, string>();
  for (const name of [...field.options, ...added, ...items.map((i) => valueOf(i, field.id))]) {
    const clean = name.trim();
    if (clean && !all.has(clean.toLowerCase())) all.set(clean.toLowerCase(), clean);
  }
  return [...all.values()].sort((a, b) => a.localeCompare(b));
}

/** How many of `items` hold each value of each choice field of `fields`, most first (fields with no value left out). */
export function valueCounts(items: readonly WithData[], fields: readonly FieldDef[]): { field: FieldDef; values: { value: string; count: number }[] }[] {
  return fields
    .filter((f) => f.kind === "choice")
    .map((field) => {
      const counts = new Map<string, number>();
      for (const item of items) {
        const value = valueOf(item, field.id);
        if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
      }
      const values = [...counts].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
      return { field, values };
    })
    .filter((c) => c.values.length);
}

/** `data` with field `id` set to `value` (trimmed at the end; empty: the field taken out). */
export function withValue(data: { [key in string]: string } | undefined, id: string, value: string): { [key in string]: string } {
  const next: { [key in string]: string } = {};
  for (const [k, v] of Object.entries(data ?? {})) if (v && k !== id) next[k] = v;
  const clean = value.replace(/\s+$/, "");
  if (clean.trim()) next[id] = clean;
  return next;
}
